import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Modal,
    IconButton,
    Typography,
    Avatar,
    Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DownloadIcon from "@mui/icons-material/Download";

interface ImagePreviewerProps {
    open: boolean;
    images: (string | File | null | undefined)[];
    onClose: () => void;
    initialIndex?: number;
}

const FALLBACK = "/default.jpg";

/** Type guard for File-like objects */
const isFileLike = (v: unknown): v is File =>
    v instanceof File ||
    (v !== null &&
        typeof v === "object" &&
        "name" in (v as any) &&
        "lastModified" in (v as any));

const getFileKeyFromFileLike = (f: File): string =>
    `${f.name}::${f.size}::${f.lastModified}`;

const getFileKeyFromMaybe = (item: string | File | null | undefined, fallbackIndex?: number): string => {
    if (typeof item === "string" && item.trim()) return item;
    if (isFileLike(item)) return getFileKeyFromFileLike(item);
    // final fallback: index-based key (guaranteed unique per render)
    return `__idx_${fallbackIndex ?? Math.random().toString(36).slice(2)}`;
};

const getFileNameSafe = (item: string | File | null | undefined): string => {
    if (isFileLike(item)) return item.name;
    if (typeof item === "string" && item) {
        const parts = item.split("/");
        return parts[parts.length - 1] || "image.jpg";
    }
    return "image.jpg";
};

const ImagePreviewer: React.FC<ImagePreviewerProps> = ({
    open,
    images,
    onClose,
    initialIndex = 0,
}) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [failed, setFailed] = useState<Record<string, boolean>>({});
    const srcMapRef = useRef<Map<string, string>>(new Map());
    const fileToBlobRef = useRef<WeakMap<File, string>>(new WeakMap());
    const prevKeysRef = useRef<Set<string>>(new Set());

    // Sync images -> srcMapRef, create blob URLs for File objects
    useEffect(() => {
        const currentKeys = new Set<string>();
        const newEntries: [string, string][] = [];

        images.forEach((item, idx) => {
            const key = getFileKeyFromMaybe(item, idx);
            currentKeys.add(key);

            if (srcMapRef.current.has(key)) return; // already mapped

            if (isFileLike(item)) {
                // reuse existing blob if same File instance
                const existing = fileToBlobRef.current.get(item);
                if (existing) {
                    newEntries.push([key, existing]);
                    srcMapRef.current.set(key, existing);
                } else {
                    const blob = URL.createObjectURL(item);
                    fileToBlobRef.current.set(item, blob);
                    newEntries.push([key, blob]);
                    srcMapRef.current.set(key, blob);
                }
            } else if (typeof item === "string" && item.trim()) {
                srcMapRef.current.set(key, item);
            } else {
                // null/undefined/unexpected -> fallback
                srcMapRef.current.set(key, FALLBACK);
            }
        });

        // revoke & delete removed keys (only revoke blob: URLs we created)
        const prevKeys = prevKeysRef.current;
        prevKeys.forEach((k) => {
            if (!currentKeys.has(k)) {
                const url = srcMapRef.current.get(k);
                if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
                srcMapRef.current.delete(k);
                setFailed((p) => {
                    const copy = { ...p };
                    delete copy[k];
                    return copy;
                });
            }
        });

        prevKeysRef.current = currentKeys;

        // adjust currentIndex if needed
        setCurrentIndex((ci) => {
            const max = Math.max(0, images.length - 1);
            return ci > max ? max : ci;
        });

        // no global cleanup here — handled when items are removed above
        // (and we'll also revoke any lingering blobs on unmount)
        return () => {
        };
    }, [images]);

    // Revoke any remaining blob URLs on unmount
    useEffect(() => {
        return () => {
            srcMapRef.current.forEach((v) => {
                if (v && v.startsWith("blob:")) URL.revokeObjectURL(v);
            });
            srcMapRef.current.clear();
            prevKeysRef.current.clear();
        };
    }, []);

    useEffect(() => {
        setCurrentIndex(initialIndex);
    }, [initialIndex]);

    if (!images || images.length === 0) return null;

    const showArrows = images.length > 1;
    const currentItem = images[currentIndex];
    const currentKey = getFileKeyFromMaybe(currentItem, currentIndex);
    const currentSrc = srcMapRef.current.get(currentKey) ?? (typeof currentItem === "string" ? currentItem || FALLBACK : FALLBACK);
    const currentFileName = getFileNameSafe(currentItem);

    const handlePrev = () => setCurrentIndex((p) => (p > 0 ? p - 1 : images.length - 1));
    const handleNext = () => setCurrentIndex((p) => (p < images.length - 1 ? p + 1 : 0));
    const handleSelect = (idx: number) => setCurrentIndex(idx);

    const handleImageErrorByKey = (key: string) => {
        setFailed((prev) => ({ ...prev, [key]: true }));
    };

    const handleDownload = async () => {
        try {
            const src = srcMapRef.current.get(currentKey) ?? (typeof currentItem === "string" ? currentItem || FALLBACK : FALLBACK);
            const filename = getFileNameSafe(currentItem);
            const response = await fetch(src, { mode: "cors" });
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error("download failed", err);
        }
    };

    return (
        <Modal open={open} onClose={onClose} sx={{ zIndex: 2000 }}>
            <Box
                sx={{
                    position: "fixed",
                    inset: 0,
                    bgcolor: "rgba(0,0,0,0.8)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 2,
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        position: "absolute",
                        top: 16,
                        left: 0,
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        px: 3,
                    }}
                >
                    <Typography variant="h6" color="white">
                        {currentFileName} ({currentIndex + 1}/{images.length})
                    </Typography>
                    <Box>
                        <IconButton onClick={handleDownload} sx={{ color: "white" }} disabled={!!failed[currentKey]}>
                            <DownloadIcon />
                        </IconButton>
                        <IconButton onClick={onClose} sx={{ color: "white" }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>

                {/* Main */}
                <Box
                    sx={{
                        position: "relative",
                        width: "80%",
                        height: "70%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {showArrows && (
                        <IconButton onClick={handlePrev} sx={{ position: "absolute", left: 16, color: "white", bgcolor: "rgba(0,0,0,0.3)", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}>
                            <ChevronLeftIcon fontSize="large" />
                        </IconButton>
                    )}

                    <Box
                        component="img"
                        src={failed[currentKey] ? FALLBACK : currentSrc}
                        alt={currentFileName}
                        onError={() => handleImageErrorByKey(currentKey)}
                        sx={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            borderRadius: 2,
                            boxShadow: "0 0 20px rgba(0,0,0,0.5)",
                            transition: "0.3s ease-in-out",
                        }}
                    />

                    {showArrows && (
                        <IconButton onClick={handleNext} sx={{ position: "absolute", right: 16, color: "white", bgcolor: "rgba(0,0,0,0.3)", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}>
                            <ChevronRightIcon fontSize="large" />
                        </IconButton>
                    )}
                </Box>

                {/* Thumbnails */}
                <Stack direction="row" spacing={1.5} sx={{ position: "absolute", bottom: 24, overflowX: "auto", px: 3 }}>
                    {images.map((it, idx) => {
                        const k = getFileKeyFromMaybe(it, idx);
                        const s = srcMapRef.current.get(k) ?? (typeof it === "string" ? it || FALLBACK : FALLBACK);
                        const filename = getFileNameSafe(it);
                        return (
                            <Box key={k} sx={{ display: "flex", flexDirection: "column", alignItems: "center", cursor: "pointer" }} onClick={() => handleSelect(idx)}>
                                <Avatar src={failed[k] ? FALLBACK : s} variant="square" onError={() => handleImageErrorByKey(k)} sx={{ width: 64, height: 64, border: currentIndex === idx ? "3px solid white" : "2px solid transparent", opacity: currentIndex === idx ? 1 : 0.6, transition: "all 0.2s ease-in-out", "&:hover": { opacity: 1, transform: "scale(1.05)" } }} />
                                <Typography variant="caption" sx={{ mt: 0.5, color: "white", maxWidth: 70, textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={filename}>
                                    {filename}
                                </Typography>
                            </Box>
                        );
                    })}
                </Stack>
            </Box>
        </Modal>
    );
};

export default ImagePreviewer;
